import React, { FC, useContext, useEffect, useState } from "react";
import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core";

import Toolbar from "@material-ui/core/Toolbar";
import CircularProgress from "@material-ui/core/CircularProgress";

import { GreenButton } from "../../../lib/components/ColorButtons";
import { GridColumns } from "./gridsetup";
import Grid from "../../../lib/components/grid";
import { ProductGroup, productgroup } from "../../../lib/api/productgroup";
import { SnackContext } from "../../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../../lib/helpers/string_methods";
import Confirmation from "../../../lib/components/confirmation";
import { LabelingForm } from "./form";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
    },
    toolbarWrapper: {
      position: "relative",
    },
    toolbar: {
      position: "absolute",
      left: 0,
      width: "100%",
      gap: `${theme.spacing(1)}px`,
    },
  });

type ProductGroupProps = {} & WithStyles<typeof styles>;

const ProductGroupGridUnstyled: FC<ProductGroupProps> = ({ classes }) => {
  const { updateSnack } = useContext(SnackContext);

  const [data, setData] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(0);
  const [deleteId, setDeleteId] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await productgroup.all();
      setData(result);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to fetch data. Please try again");
      updateSnack({ show: true, color: "red", message: err });
    }
    setLoading(false);
  };

  const handleRemove = (id: number) => {
    setDeleteId(id);
  };

  const handleEdit = (id: number) => {
    setEditing(id);
  };

  const handlePostSubmit = () => {
    setEditing(0);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleConfirmDelete = async () => {
    try {
      await productgroup.remove(deleteId);
      updateSnack({ show: true, color: "green", message: "Successfully removed!" });
      loadData();
      setDeleteId(0);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to delete. Please try again");
      updateSnack({ show: true, color: "red", message: err });
    }
  };

  return (
    <div>
      <Confirmation body={null} handleClose={() => setEditing(0)} handleConfirm={() => { }} isOpen={Boolean(editing)} title="Target Market">
        <LabelingForm id={editing} handleClose={() => handlePostSubmit()} />
      </Confirmation>
      <Confirmation
        isOpen={deleteId}
        handleClose={() => setDeleteId(0)}
        handleConfirm={handleConfirmDelete}
        title={"Delete confirmation"}
        body={"Are you sure you want to remove this?"}
      />
      <div className={classes.toolbarWrapper}>
        <Toolbar className={classes.toolbar}>
          {loading && <CircularProgress size={25} style={{ marginRight: "1rem" }} />}
          <GreenButton variant="contained" onClick={() => setEditing(-1)} disabled={loading}>
            Add
          </GreenButton>
        </Toolbar>
        <Grid
          data={data}
          clearFilters="productgroup"
          GridColumns={(data, filters, arrangement, columnsWidth) => GridColumns(data, filters, arrangement, columnsWidth, handleRemove, handleEdit)}
        />
      </div>
    </div>
  );
};

export const ProductGroupGrid = withStyles(styles)(ProductGroupGridUnstyled);
